import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useProductCenterApi } from 'api';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { useDialogState } from 'library/hooks/useDialogState';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';
import { home_HomeUrl } from 'main/routes/urls/RouteUrls';

interface EditDeleteButtonProps {
	unitId: number;
}

const EditDeleteButton = ({
	unitId,
}: EditDeleteButtonProps): React.JSX.Element => {
	const { handleOpen, open, handleClose } = useDialogState();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const [isDeleting, setIsDeleting] = useState(false);
	const { t } = useTranslation(['common', 'commonButton', 'unitEdit']);
	const { api } = useProductCenterApi();

	const handleOnAccept = useCallback(() => {
		setIsDeleting(true);
		Promise.all([
			api.deleteUnit(unitId.toString()),
			api.deleteRecentlyVisitedInfo(unitId),
		])
			.then(() => {
				enqueueSnackbar(t('unitEdit:remove-unit-success'), {
					variant: 'success',
				});
				navigate(home_HomeUrl);
			})
			.catch((error) => {
				checkConsoleDebuggingReadAccess() &&
					console.error(`Error while Deleting unit: ${error}`);
				enqueueSnackbar(t('unitEdit:remove-unit-error'), {
					variant: 'error',
				});
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}, [
		t,
		api,
		enqueueSnackbar,
		navigate,
		unitId,
		checkConsoleDebuggingReadAccess,
	]);

	return (
		<>
			<Button
				variant="outlined"
				color="secondary"
				size="large"
				onClick={handleOpen}
				sx={{ mr: 'auto' }}>
				{t('commonButton:delete')}
			</Button>
			<CustomSeverityDialog
				isOpen={open}
				acceptButtonLabel={t('commonButton:yes')}
				declineButtonLabel={t('commonButton:no')}
				onDecline={handleClose}
				content={t('unitEdit:remove-unit-question')}
				headerText={t('commonButton:delete')}
				onAccept={handleOnAccept}
				disabledAcceptButton={isDeleting}
				severity="error"
			/>
		</>
	);
};

export default EditDeleteButton;
